<!----
    Author: hjlee
    Description:
        메모
---->
<template>
  <div v-if="memo" class="memo" :type="type" :priority="memo.priortCode">
    <div class="memo-summary clickable" @click="toggleCollapse">
      <div v-if="useTitle" class="memo-summary-text">
        {{ memo.title }}
      </div>
      <div v-else class="memo-summary-text" v-html="memo.cn" />
      -
      <span v-if="memo.writer" class="writer-name">{{ memo.writer.nm }}</span>
      <span v-else class="writer-name">{{ $t('common.text.deleted_user') }}</span>
      <span class="collapse-memo">
        <i v-if="collapsed" class="fa fa-angle-down" />
        <i v-else class="fa fa-angle-up" />
      </span>
      <span class="registered-operation-date">{{ memo.rgsOperDt | formatDatetime }}</span>
      <div v-if="memo.rgsOperId === loggedInUser.id" class="modify-memo-control-area">
        <a @click="modifyMemo($event)">
          <i class="fa fa-pencil" />
        </a>
        <a @click="deleteMemoConfirm($event)">
          <i class="fa fa-trash" />
        </a>
      </div>
    </div>
    <div v-if="!collapsed" class="memo-detail">
      <div class="memo-writer">
        <span v-if="memo.writer" class="writer-name">{{ memo.writer.nm }}</span>
        <span v-else class="writer-name">{{ $t('common.text.deleted_user') }}</span>
      </div>
      <div v-if="useTitle" class="memo-title">
        <span v-if="mode==='view'">
          {{ memo.title }}
        </span>
        <input-control
          v-else
          v-model="memoTarget.title"
          :required="true"
          name="title"
          type="text"
          :no-title="true"
          :placeholder="$t('column.nm.title')"
          :title="$t('column.nm.title')"
        />
      </div>
      <div class="rows">
        <div v-if="usePriority" class="memo-priority cols-sm-6">
          <input-control
            v-if="priorityOptions"
            v-model="memo.priortCode"
            type="select"
            :title="$t('code_group.nm.memo_priority')"
            :options="priorityOptions"
            :no-title="true"
            name="priortCode"
            :readonly="readonly"
            :disabled="disabled"
          />
        </div>
        <div v-if="sttusCodeGroup" class="memo-status cols-sm-6">
          <input-control
            v-if="statusOptions"
            v-model="memo.sttusCode"
            type="select"
            :title="$t('code_group.nm.memo_status')"
            :options="statusOptions"
            :no-title="true"
            name="sttusCode"
            :readonly="readonly"
            :disabled="disabled"
          />
        </div>
        <div v-if="useCharger" class="memo-charger cols-sm-6">
          <input-control
            v-model="memo.chargerMap"
            type="multiselect"
            name="charger_id"
            :options="chargerOptions"
            :multiple="false"
            async-search-url="/user/search"
            :placeholder="$t('memo.text.charger')"
            :title="$t('memo.text.charger')"
            :readonly="readonly"
            :disabled="disabled"
          />
        </div>
        <div v-if="useAttachment" class="memo-attachments cols-sm-6">
          <div v-for="(file, fileIndex) in memo.attachments" :key="'file-' + file.id + '-' + fileIndex" class="file-item">
            <a class="clickable" @click="downloadFileWithTmpId(file.tmpId, file.fileNm, file)">
              <file-icon :filename="file.fileNm" scale="extra-small" />
              {{ file.fileNm }}
              <span class="download-count">
                <i class="fa fa-download" /> {{ file.dwldCo }}
              </span>
            </a>
          </div>
        </div>
      </div>
      <div class="clear" />
      <div v-if="mode === 'view'" class="memo-contents" v-html="memo.cn" />
      <input-control
        v-else
        v-model="memoTarget.cn"
        :required="true"
        name="cn"
        type="html_editor"
        :editor-options="editorOptions"
        :no-title="true"
        :placeholder="$t('column.nm.cn')"
        :title="$t('column.nm.cn')"
      />
      <div v-if="mode === 'modify'" class="update-control-area">
        <button class="btn-sm btn-primary" @click="updateMemoConfirm($event)">
          {{ $t('common.button.save') }}
        </button>
        <button class="btn-sm btn-secondary" @click="cancelModification($event)">
          {{ $t('common.button.cancel') }}
        </button>
      </div>
      <div v-if="!disabled && !readonly" class="insert-memo-comment-control">
        <div class="insert-memo-comment-control-title clickable" @click="toggleCollapseInput">
          {{ $t('memo.placeholder.insert_memo_comment') }}
          <div class="collapse-button">
            <i v-if="inputCollapsed" class="fa fa-angle-down" />
            <i v-else class="fa fa-angle-up" />
          </div>
        </div>
        <div v-if="!inputCollapsed" class="input-control-area">
          <input-control
            v-model="newMemoComment.cn"
            :required="true"
            name="cn"
            type="html_editor"
            :editor-options="editorOptions"
            :no-title="true"
            :placeholder="$t('column.nm.cn')"
            :title="$t('column.nm.cn')"
          />
          <div v-if="options.useAttachment" class="file-attachment-control">
            <div class="file-attachment-summary clickable" @click="toggleCollapseFileAttachment()">
              <span>{{ $t('memo.button.attach_file') }}</span>
              <div class="collapse-button">
                <i v-if="fileAttachmentCollapsed" class="fa fa-angle-down" />
                <i v-else class="fa fa-angle-up" />
              </div>
            </div>
            <input-control
              v-if="!fileAttachmentCollapsed"
              v-model="newMemoComment.attachments"
              name="attachments"
              type="multifile"
              table-name="memo_comment"
              :file-info="{'purpose': 'file_purpose.general'}"
              :no-title="true"
              :placeholder="$t('column.nm.attachment')"
              :title="$t('column.nm.attachment')"
            />
          </div>
          <div class="insert-button-area">
            <button class="form-control btn btn-primary" @click="addMemoComment()">
              {{ $t('common.button.insert') }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="memo.memoComments && memo.memoComments.length > 0">
        <memo-comment
          v-for="(memoComment, memoCommentIndex) in memo.memoComments"
          :key="'memo-comment-' + memo.id + '-' + memoComment.id"
          :index="memoCommentIndex"
          :readonly="readonly"
          :disabled="disabled"
          :memo-comment="memoComment"
          :editor-options="editorOptions"
          class="memo-comment-item"
          @updated="onUpdated"
          @deleted="onDeleted"
        />
        <div v-if="isBusy">
          <img src="~assets/img/ajax-loader-white-bg.gif" alt="Loading...">
        </div>
        <button v-if="loadedSize < totalSize" class="form-control btn btn-light" @click="loadMemoComments(true)">
          {{ $t('common.button.view_more') }}
        </button>
      </div>
      <div v-else class="no-memo-comment">
        {{ $t('memo.text.no_memo_comment') }}
      </div>
    </div>
    <b-modal
      id="change-priority-confirm-modal"
      ref="changePriorityConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="changePriority"
      @cancel="cancelChangePriority"
    >
      <p class="my-4" v-html="$t('memo.confirm.change_priority', {priority: !memo ? '' : $t('code.nm.' + memo.priortCode)})" />
    </b-modal>
    <b-modal
      id="change-status-confirm-modal"
      ref="changeStatusConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="changeStatus"
      @cancel="cancelChangeStatus"
    >
      <p class="my-4" v-html="$t('memo.confirm.change_status', {status: !memo ? '' : $t('code.nm.' + memo.sttusCode)})" />
    </b-modal>
    <b-modal
      id="change-charger-confirm-modal"
      ref="changeChargerConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="changeCharger"
      @cancel="cancelChangeCharger"
    >
      <p class="my-4" v-html="$t('memo.confirm.change_charger', {charger: memo.chargerMap ? memo.chargerMap.text : ''})" />
    </b-modal>
    <b-modal
      id="update-confirm-modal"
      ref="updateConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="updateMemo"
    >
      <p class="my-4" v-html="$t('common.modal.text.update_confirm')" />
    </b-modal>
    <b-modal
      id="delete-confirm-modal"
      ref="deleteConfirmModalRef"
      :title="$t('common.modal.title.confirm')"
      :ok-title="$t('common.button.ok')"
      :cancel-title="$t('common.button.cancel')"
      :lazy="true"
      :no-close-on-backdrop="true"
      @ok="deleteMemo"
    >
      <p class="my-4" v-html="$t('common.modal.text.delete_confirm')" />
    </b-modal>
  </div>
</template>

<script>
export default {
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tableKey: {
      type: [Number, String],
      default: undefined
    },
    useComment: {
      type: Boolean,
      default: true
    },
    useCharger: {
      type: Boolean,
      default: false
    },
    useTitle: {
      type: Boolean,
      default: false
    },
    usePriority: {
      type: Boolean,
      default: false
    },
    useAttachment: {
      type: Boolean,
      default: true
    },
    forTable: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'normal'
    },
    sttusCodeGroup: {
      type: String,
      default: ''
    },
    time: {
      type: Number,
      default: undefined
    },
    data: {
      type: Object,
      required: true
    },
    editorOptions: {
      type: Object,
      default: undefined
    },
    priorityOptions: {
      type: Array,
      default: undefined
    },
    statusOptions: {
      type: Array,
      default: undefined
    }
  },
  data() {
    return {
      memos: [],
      mode: 'view',
      emergencyMemos: [],
      options: {},
      collapsed: true,
      chargerOptions: [],
      memo: undefined,
      inputCollapsed: true,
      fileAttachmentCollapsed: true,
      newMemoComment: {},
      isBusy: false,
      totalSize: undefined,
      loadedSize: 0,
      memoTarget: {}
    }
  },
  watch: {
    'memo.priortCode': {
      handler(newVal, oldVal) {
        this.memo.oldPriortCode = oldVal
        if (!this.doNotShowChangePriorityConfirm) {
          this.showModal('changePriorityConfirmModalRef')
        }
        this.doNotShowChangePriorityConfirm = false
      }
    },
    'memo.sttusCode': {
      handler(newVal, oldVal) {
        this.memo.oldSttusCode = oldVal
        if (!this.doNotShowChangeStatusConfirm) {
          this.showModal('changeStatusConfirmModalRef')
        }
        this.doNotShowChangeStatusConfirm = false
      }
    },
    'memo.chargerMap': {
      handler(newVal, oldVal) {
        this.memo.oldChargerMap = oldVal
        let diff = false
        if (newVal && !oldVal) {
          diff = true
        } else if (!newVal && oldVal) {
          diff = true
        } else if (!newVal && !oldVal) {
          diff = false
        } else if (newVal.data.id !== oldVal.data.id) {
          diff = true
        }
        if (!this.doNotShowChangeChargerConfirm && diff) {
          this.showModal('changeChargerConfirmModalRef')
        }
        this.doNotShowChangeChargerConfirm = false
      }
    }
  },
  created() {
    this.doNotShowChangePriorityConfirm = true
    this.doNotShowChangeStatusConfirm = true
    if (this.data.charger) {
      this.doNotShowChangeChargerConfirm = true
    }
    this.memo = window.copyObject(this.data)
    if (this.memo.charger) {
      this.chargerOptions = [
        {
          text: this.data.charger.nm + '-' + this.data.charger.userId,
          value: this.data.charger.id,
          data: this.data.charger
        }
      ]
      this.doNotShowChangeChargerConfirm = true
      this.memo.chargerMap = this.chargerOptions[0]
    } else {
      this.chargerOptions = []
    }
    if (this.memoOptions) {
      this.options = this.memoOptions
    }
    if (this.useComment !== undefined) {
      this.options.useComment = this.useComment
    }
    if (this.useCharger !== undefined) {
      this.options.useCharger = this.useCharger
    }
    if (this.useTitle !== undefined) {
      this.options.useTitle = this.useTitle
    }
    if (this.useAttachment !== undefined) {
      this.options.useAttachment = this.useAttachment
    }
    if (this.usePriority !== undefined) {
      this.options.usePriority = this.usePriority
    }
    if (this.forTable !== undefined) {
      this.options.forTable = this.forTable
    }
    if (this.type !== undefined) {
      this.options.type = this.type
    }
    if (this.sttusCodeGroup !== undefined) {
      this.options.sttusCodeGroup = this.sttusCodeGroup
    }
    this.loadMemoComments()
  },
  methods: {
    modifyMemo(e) {
      e.preventDefault()
      e.stopPropagation()
      this.memoTarget = window.copyObject(this.memo)
      this.mode = 'modify'
      this.collapsed = false
    },
    cancelModification(e) {
      e.preventDefault()
      e.stopPropagation()
      this.mode = 'view'
    },
    async updateMemoConfirm(e) {
      e.preventDefault()
      e.stopPropagation()
      if (
        !(await this.validateUnder(
          $(this.$el)
            .find('.memo-detail')
            .get(0),
          '.app-body'
        ))
      ) {
        return
      }
      this.showModal('updateConfirmModalRef')
    },
    updateMemo() {
      this.$axios.$put('/memo/memo', this.memoTarget).then(response => {
        if (response.resultCode === 'success') {
          this.$toast.info(this.$t(response.message, response.params))
          this.$emit('updated', this.memoTarget)
          this.memo = this.memoTarget
          this.mode = 'view'
        } else {
          console.error(this.$t(response.message, response.params))
          this.$toast.error(this.$t(response.message, response.params))
        }
      })
    },
    deleteMemoConfirm(e) {
      e.preventDefault()
      e.stopPropagation()
      this.showModal('deleteConfirmModalRef')
    },
    deleteMemo() {
      this.$axios.$put('/memo/deleteMemo', this.memo).then(response => {
        if (response.resultCode === 'success') {
          this.$toast.info(this.$t(response.message, response.params))
          this.$emit('deleted', this.memo)
        } else {
          console.error(this.$t(response.message, response.params))
          this.$toast.error(this.$t(response.message, response.params))
        }
      })
    },
    onDeleted(row) {
      if (this.memo.memoComments && this.memo.memoComments.length > 0) {
        for (let i = 0; i < this.memo.memoComments.length; i++) {
          if (this.memo.memoComments[i].id === row.id) {
            this.memo.memoComments.splice(i, 1)
            break
          }
        }
      }
      this.$forceUpdate()
    },
    onUpdated(row) {
      if (this.memo.memoComments && this.memo.memoComments.length > 0) {
        for (let i = 0; i < this.memo.memoComments.length; i++) {
          if (this.memo.memoComments[i].id === row.id) {
            this.memo.memoComments[i] = row
            this.$forceUpdate()
            break
          }
        }
      }
    },
    async addMemoComment() {
      if (
        !(await this.validateUnder(
          $(this.$el)
            .find('.insert-memo-comment-control')
            .get(0),
          '.app-body'
        ))
      ) {
        return
      }
      const url = '/memo/memoComment'
      const params = JSON.parse(JSON.stringify(this.newMemoComment))
      params.memoId = this.memo.id
      this.$axios.$post(url, params).then(response => {
        if (response.resultCode === 'success') {
          this.newMemoComment = {}
          this.loadMemoComments()
          this.inputCollapsed = true
        } else {
          console.error(this.$t(response.message, response.params))
          this.$toast.error(this.$t(response.message, response.params))
        }
      })
    },
    loadMemoComments(append) {
      if (!append) {
        this.loadedSize = 0
      }
      this.isBusy = true
      let url = '/memo/memoComment?memoId=' + this.memo.id
      if (append && this.lastId) {
        url += '&lastId=' + this.lastId
      }
      this.$axios
        .$get(url, { no_loading: true })
        .then(response => {
          this.isBusy = false
          if (response.resultCode === 'success') {
            if (!this.memo.memoComments) {
              this.memo.memoComments = []
            }
            if (append) {
              this.memo.memoComments = this.memo.memoComments.concat(
                response.data.list
              )
            } else {
              this.memo.memoComments = response.data.list
            }
            const memoComments = this.memo.memoComments
            this.loadedSize += this.memo.memoComments.length
            this.totalSize = response.data.totalSize
            if (memoComments && memoComments.length > 0) {
              this.lastId = memoComments[memoComments.length - 1].id
            }
            this.$forceUpdate()
          } else {
            console.error(this.$t(response.message, response.params))
            this.$toast.error(this.$t(response.message, response.params))
          }
        })
        .catch(() => {
          this.isBusy = false
        })
    },
    toggleCollapseFileAttachment() {
      this.fileAttachmentCollapsed = !this.fileAttachmentCollapsed
    },
    toggleCollapseInput() {
      this.inputCollapsed = !this.inputCollapsed
    },
    toggleCollapse() {
      this.collapsed = !this.collapsed
    },
    changePriority() {
      this.$axios
        .$put('/memo/changePriority', this.memo)
        .then(response => {
          if (response.resultCode === 'success') {
            this.$toast.info(this.$t(response.message, response.params))
            this.$emit('updated', this.memo)
            this.loadMemoComments()
          } else {
            console.error(this.$t(response.message, response.params))
            this.$toast.error(this.$t(response.message, response.params))
            this.cancelChangePriority()
          }
        })
        .catch(() => {
          this.cancelChangePriority()
        })
    },
    cancelChangePriority() {
      this.doNotShowChangePriorityConfirm = true
      this.memo.priortCode = this.memo.oldPriortCode
    },
    changeStatus() {
      this.$axios
        .$put('/memo/changeStatus', this.memo)
        .then(response => {
          if (response.resultCode === 'success') {
            this.$toast.info(this.$t(response.message, response.params))
            this.$emit('updated', this.memo)
            this.loadMemoComments()
          } else {
            console.error(this.$t(response.message, response.params))
            this.$toast.error(this.$t(response.message, response.params))
            this.cancelChangeStatus()
          }
        })
        .catch(() => {
          this.cancelChangeStatus()
        })
    },
    cancelChangeStatus() {
      this.doNotShowChangeStatusConfirm = true
      this.memo.sttusCode = this.memo.oldSttusCode
    },
    changeCharger() {
      this.$axios
        .$put('/memo/changeCharger', this.memo)
        .then(response => {
          if (response.resultCode === 'success') {
            this.$toast.info(this.$t(response.message, response.params))
            this.$emit('updated', this.memo)
            this.loadMemoComments()
          } else {
            console.error(this.$t(response.message, response.params))
            this.$toast.error(this.$t(response.message, response.params))
            this.cancelChangeCharger()
          }
        })
        .catch(() => {
          this.cancelChangeCharger()
        })
    },
    cancelChangeCharger() {
      this.doNotShowChangeChargerConfirm = true
      this.memo.chargerMap = this.memo.oldChargerMap
    }
  }
}
</script>

<style scoped>
.memo {
  text-align: left;
  background-color: #efefef;
  margin-bottom: 5px;
  padding: 5px;
  position: relative;
}
.memo-charger {
  margin-top: 10px;
}
.memo-summary {
  position: relative;
}
.memo-summary-text {
  max-width: calc(100% - 300px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  float: left;
}
.registered-operation-date {
  float: right;
  min-width: 150px;
  font-size: 0.7rem;
  color: #777777;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px;
  margin-right: 5px;
}
.collapse-button {
  float: right;
}
.memo-detail {
  margin-top: 5px;
  padding: 10px;
  background-color: white;
  padding-bottom: 4px;
}
.insert-button-area {
  margin-top: 20px;
}
.memo-title {
  font-weight: bold;
  margin-bottom: 8px;
  word-break: break-all;
  white-space: normal;
}
.memo[priority='memo_priority.emergency'] {
  background-color: #ff9999;
}
.no-memo-comment {
  text-align: center;
  padding: 30px 0;
}
.input-control-area {
  margin-top: 5px;
}
.insert-memo-comment-control-title {
  text-align: left;
}
.insert-memo-comment-control {
  text-align: left;
  padding: 10px;
  margin-top: 5px;
  margin-bottom: 5px;
  border: 1px solid #cccccc;
}
.memo-comment-item {
  margin-bottom: 5px;
}
.writer-name {
  font-size: 0.7rem;
  color: #999999;
}
.memo-writer {
  text-align: right;
}
.file-item {
  padding: 5px 0;
}
.memo-contents {
  border: 1px solid #cccccc;
  padding: 10px;
  word-break: break-all;
  white-space: normal;
}
.collapse-memo {
  float: right;
}
>>> .input-control {
  min-height: auto !important;
}
.modify-memo-control-area {
  float: right;
}
.modify-memo-control-area a {
  margin-right: 10px;
}
.memo[type='postit'] {
  position: absolute;
  width: 400px;
  max-height: 400px;
  overflow-y: auto;
  padding-top: 0;
}
.memo[type='postit'] .memo-summary {
  position: sticky;
  top: 0;
  background-color: #eeeeee;
  padding: 5px 0;
  z-index: 10;
}
.update-control-area {
  text-align: right;
}
</style>
